import { Button, ButtonProps } from 'react-aria-components';
import { Control, UseFormRegister, FieldErrors } from 'react-hook-form';

import RadioGroup from 'src/components/RadioGroup/RadioGroup';

import DateField from '@/components/DateField/DateField';
import FileUpload from '@/components/FileUpload/FileUpload';
import InputText from '@/components/InputText/InputText';
import SelectField from '@/components/SelectField/SelectField';
import {
  otherFacilityCities,
  supportTypes,
  supportDays,
  maxMonthlyBurdenAmounts,
} from '@/logics/children/children';
import { UpdateChildFormData } from '@/pages/admin/children/[id]';
import { Child } from '@/types/child';

type Props = {
  child: Child | undefined;
  errors: FieldErrors<UpdateChildFormData>;
  control: Control<UpdateChildFormData>;
  register: UseFormRegister<UpdateChildFormData>;
  setSealRegistrationFile: (v: File | undefined) => void;
  recipientCertificateFile1: File | undefined;
  setRecipientCertificateFile1: (v: File | undefined) => void;
  recipientCertificateFile2: File | undefined;
  setRecipientCertificateFile2: (v: File | undefined) => void;
  setRecipientCertificateIsDeleted1: (v: boolean) => void;
  setRecipientCertificateIsDeleted2: (v: boolean) => void;
  recipientCertificateIsLocked1: boolean;
  setRecipientCertificateIsLocked1: (v: boolean) => void;
  recipientCertificateIsLocked2: boolean;
  setRecipientCertificateIsLocked2: (v: boolean) => void;
};

export const RecipientCertificateForm = ({
  child,
  errors,
  control,
  register,
  setSealRegistrationFile,
  recipientCertificateFile1,
  setRecipientCertificateFile1,
  recipientCertificateFile2,
  setRecipientCertificateFile2,
  setRecipientCertificateIsDeleted1,
  setRecipientCertificateIsDeleted2,
  recipientCertificateIsLocked1,
  setRecipientCertificateIsLocked1,
  recipientCertificateIsLocked2,
  setRecipientCertificateIsLocked2,
}: Props) => {
  type Props = ButtonProps & {
    onPress: () => void;
  };
  const LockButton = (props: Props) => {
    return (
      <Button
        className="flex cursor-pointer items-center gap-2 rounded-md border border-red-600 px-4 py-2 text-red-600 hover:text-red-700"
        {...props}
      >
        <LockedIcon />
        <span>受給者証ロック</span>
      </Button>
    );
  };
  const DeleteButton = (props: Props) => {
    return (
      <Button
        className="flex cursor-pointer items-center gap-2 rounded-md px-4 py-2"
        {...props}
      >
        <DeleteIcon />
      </Button>
    );
  };
  return (
    <div className="flex flex-col justify-center">
      <div className="flex flex-row gap-4">
        <RadioGroup
          title="上限管理"
          required={true}
          errors={errors.hasUpperManagement}
          options={[
            {
              id: 'true',
              label: '有',
              value: 'true',
            },
            {
              id: 'false',
              label: '無',
              value: 'false',
            },
          ]}
          initialChecked={child?.has_upper_management ? 'true' : 'false'}
          {...register('hasUpperManagement')}
        />
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="他事業所名（1）"
          type="text"
          required={false}
          {...register('otherFacilityName1')}
        />
        <SelectField
          name="otherFacilityCity1"
          label="他事業所（1）（市区町村）"
          control={control}
          items={otherFacilityCities.map((otherFacilityCity) => ({
            label: otherFacilityCity,
            value: otherFacilityCity,
          }))}
          required={false}
        />
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="他事業所名（2）"
          type="text"
          required={false}
          {...register('otherFacilityName2')}
        />
        <SelectField
          name="otherFacilityCity2"
          label="他事業所（2）（市区町村）"
          control={control}
          items={otherFacilityCities.map((otherFacilityCity) => ({
            label: otherFacilityCity,
            value: otherFacilityCity,
          }))}
          required={false}
        />
      </div>
      <div className="flex flex-row items-center">
        <div className="flex flex-col">
          <FileUpload
            label="書類確認用印鑑登録"
            buttonText="画像アップロード"
            required={false}
            setFile={setSealRegistrationFile}
            initialValue={child?.seal_registration_image_url || ''}
            canPreview={true}
            accept="image/*"
          />
        </div>
      </div>
      <div className="flex w-1/2 flex-col gap-4">
        <InputText
          label="受給者証番号"
          type="text"
          required={true}
          {...register('recipientCertificateNumber')}
        />
      </div>
      <div className="flex flex-row items-end gap-4">
        <div className="flex flex-col">
          <FileUpload
            label="受給者証（1）"
            buttonText="画像アップロード"
            required={true}
            setFile={setRecipientCertificateFile1}
            initialValue={child?.recipient_certificates?.[0].image_url || ''}
            canPreview={false}
            disabled={recipientCertificateIsLocked1}
            accept="image/*"
          />
        </div>
        <div className="mb-2 flex flex-row gap-2">
          {(recipientCertificateFile1 || child?.recipient_certificates?.[0].image_url) && (
            <DeleteButton onPress={() => setRecipientCertificateIsDeleted1(true)} />
          )}
          {recipientCertificateIsLocked1 ? (
            <Button onPress={() => setRecipientCertificateIsLocked1(false)}>
              受給者証ロック解除
            </Button>
          ) : (
            <LockButton onPress={() => setRecipientCertificateIsLocked1(true)} />
          )}
        </div>
      </div>
      <div className="flex flex-row gap-4">
        <DateField
          label="受給者証（1）/適用期間開始"
          required={true}
          errors={errors.recipientCertificateStartDate1}
          control={control}
          name="recipientCertificateStartDate1"
        />
        <DateField
          label="受給者証（1）/適用期間終了"
          required={true}
          errors={errors.recipientCertificateEndDate1}
          control={control}
          name="recipientCertificateEndDate1"
        />
      </div>
      <div className="flex flex-row items-end gap-4">
        <div className="flex flex-col">
          <FileUpload
            label="受給者証（2）"
            buttonText="画像アップロード"
            required={false}
            setFile={setRecipientCertificateFile2}
            initialValue={
              child?.recipient_certificates && child?.recipient_certificates.length > 1
                ? child?.recipient_certificates?.[1].image_url
                : ''
            }
            canPreview={false}
            disabled={recipientCertificateIsLocked2}
            accept="image/*"
          />
        </div>
        <div className="mb-2 flex flex-row gap-2">
          {(recipientCertificateFile2 ||
            (child?.recipient_certificates &&
              child?.recipient_certificates.length > 1 &&
              child?.recipient_certificates?.[1].image_url)) && (
            <DeleteButton onPress={() => setRecipientCertificateIsDeleted2(true)} />
          )}
          {recipientCertificateIsLocked2 ? (
            <Button onPress={() => setRecipientCertificateIsLocked2(false)}>
              受給者証ロック解除
            </Button>
          ) : (
            <LockButton onPress={() => setRecipientCertificateIsLocked2(true)} />
          )}
        </div>
      </div>
      <div className="flex flex-row gap-4">
        <DateField
          label="受給者証（2）/適用期間開始"
          required={false}
          errors={errors.recipientCertificateStartDate2}
          control={control}
          name="recipientCertificateStartDate2"
        />
        <DateField
          label="受給者証（2）/適用期間終了"
          required={false}
          errors={errors.recipientCertificateEndDate2}
          control={control}
          name="recipientCertificateEndDate2"
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="supportType"
          label="支援の種類"
          control={control}
          items={supportTypes.map((supportType) => ({
            label: supportType.label,
            value: supportType.value,
          }))}
          required={true}
        />
        <SelectField
          name="supportDays"
          label="支給量等"
          control={control}
          items={supportDays.map((supportDay) => ({
            label: supportDay + '日',
            value: supportDay,
          }))}
          required={true}
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="maxMonthlyBurdenAmount"
          label="負担上限額"
          control={control}
          items={maxMonthlyBurdenAmounts.map((maxMonthlyBurdenAmount) => ({
            label: maxMonthlyBurdenAmount + '円',
            value: maxMonthlyBurdenAmount,
          }))}
          required={true}
        />
        <InputText
          label="特記事項（受給者証）"
          type="text"
          required={false}
          {...register('recipientCertificateNotes')}
        />
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="保護者氏名（受給者証に記載のもの）"
          type="text"
          required={false}
          errors={errors.recipientCertificateGuardianName}
          {...register('recipientCertificateGuardianName')}
        />
        <DateField
          label="保護者生年月日（受給者証に記載のもの）"
          required={false}
          errors={errors.recipientCertificateGuardianBirthDate}
          control={control}
          {...register('recipientCertificateGuardianBirthDate')}
        />
      </div>
    </div>
  );
};

export const LockedIcon = () => {
  return (
    <svg
      width="15"
      height="16"
      viewBox="0 0 15 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.4196 5.44883H11.5635V4.56907C11.5635 4.46665 11.5584 4.36549 11.5509 4.26494H11.5635C11.4076 2.16283 9.64744 0.5 7.50613 0.5C7.50417 0.5 7.50223 0.500176 7.50027 0.500176C7.49831 0.500176 7.49637 0.5 7.49441 0.5C5.35313 0.5 3.59298 2.16283 3.43709 4.26494H3.44963C3.44216 4.36549 3.43709 4.46665 3.43709 4.56907V5.44883H1.58095C0.915388 5.44883 0.385613 6.00632 0.419509 6.67106L0.841209 14.3963C0.872761 15.0148 1.38341 15.5 2.00265 15.5H12.9979C13.6172 15.5 14.1278 15.0148 14.1594 14.3963L14.5811 6.67106C14.615 6.00632 14.0852 5.44883 13.4196 5.44883ZM8.10434 10.5143V12.1977C8.10434 12.4875 7.86719 12.7247 7.57735 12.7247C7.28749 12.7247 7.05036 12.4875 7.05036 12.1977V10.5143C6.71113 10.3273 6.47857 9.97054 6.47857 9.55575C6.47857 8.94893 6.97053 8.45694 7.57735 8.45694C8.18418 8.45694 8.67613 8.94893 8.67613 9.55575C8.67613 9.97057 8.4436 10.3273 8.10434 10.5143ZM9.94164 5.44883H5.05896V4.56907C5.05896 3.22177 6.1537 2.12519 7.50027 2.12202C8.84687 2.12519 9.94161 3.22174 9.94161 4.56907V5.44883H9.94164Z"
        fill="#FA0000"
      />
    </svg>
  );
};

export const DeleteIcon = () => {
  return (
    <svg
      width="31"
      height="32"
      viewBox="0 0 31 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="15.5"
        cy="16"
        r="14.5"
        fill="white"
        stroke="#0031D8"
        strokeWidth="2"
      />
      <mask
        id="mask0_1010_8940"
        // style="mask-type:alpha"
        maskUnits="userSpaceOnUse"
        x="5"
        y="6"
        width="20"
        height="19"
      >
        <rect
          x="5.69971"
          y="6.19971"
          width="18.6"
          height="18.6"
          fill="#D9D9D9"
        />
      </mask>
      <g mask="url(#mask0_1010_8940)">
        <path
          d="M10.6599 20.6532L9.84619 19.8395L14.1862 15.4995L9.84619 11.1595L10.6599 10.3457L14.9999 14.6857L19.3399 10.3457L20.1537 11.1595L15.8137 15.4995L20.1537 19.8395L19.3399 20.6532L14.9999 16.3132L10.6599 20.6532Z"
          fill="#0031D8"
        />
      </g>
    </svg>
  );
};
